import { IPromise, copy } from "angular";
import { AssigneeAvatar } from "@gtmhub/assignees";
import { unique } from "@gtmhub/util";

export type IDynamicValue =
  | { type: "me"; evaluated?: string[] }
  | { type: "myTeams"; evaluated?: string[] }
  | { type: "activeSessions"; evaluated?: string[] }
  | { type: "teamPlusMembers"; teamId: string; evaluated?: string[] }
  | { type: "teamOrg"; teamId: string; evaluated?: string[] };

export type DynamicValueType = IDynamicValue["type"];

export const dynamicValueEquals = (a: IDynamicValue, b: IDynamicValue): boolean => {
  if ((a.type === "teamPlusMembers" && b.type === "teamPlusMembers") || (a.type === "teamOrg" && b.type === "teamOrg")) {
    return a.teamId === b.teamId;
  }

  return a.type === b.type;
};

export interface IGridFilterViewBase<Filter extends IGridFilter, Definition extends IGridFilterDefinition, ViewValue> {
  fieldName: string;
  definition: Definition;
  filter: Filter;
  viewValue?: ViewValue;
}

export interface IGridFilterBase<Value> {
  fieldName: string;
  operator: GridFilterOperator;
  value?: Value;
}

interface IGridFilterDefinitionBase<DefaultValue> {
  trackingName?: string;
  displayName: string;
  fieldName: string;
  supportedOperators: GridFilterOperator[];
  defaultOperator: GridFilterOperator;
  isCustomField?: boolean;
  defaultValue?: DefaultValue;
  readonly?: boolean;
  maxValues?: number;
}

export type IArrayGridFilterView = IGridFilterViewBase<IArrayGridFilter, IArrayGridFilterDefinition, IGridFilterTag[]>;

export type GridFilterEntityDataItem = { displayName: string; value: unknown };

export interface IArrayGridFilter extends IGridFilterBase<string[]> {
  dataType: "array";
  dynamicValues?: IDynamicValue[];
  entityData?: GridFilterEntityDataItem[];
}

export interface IArrayGridFilterEntity {
  name: string;
  valueField: string;
  displayNameField: string;
}

export interface IStaticArrayGridFilterDefinition extends IGridFilterDefinitionBase<string[]> {
  dataType: "array";
  arrayType: "static";
  entries: GridFilterEntry[];
  placeholder?: string;
  keepOrder?: boolean;
}

export interface IEntityArrayGridFilterDefinition extends IGridFilterDefinitionBase<string[]> {
  dataType: "array";
  arrayType: "entity";
  entity: IArrayGridFilterEntity;
  placeholder?: string;
}

export interface ISessionArrayGridFilterDefinition extends IGridFilterDefinitionBase<string[]> {
  dataType: "array";
  arrayType: "session";
  hostSessionId?: string;
  placeholder?: string;
}
export interface IGeneralArrayGridFilterDefinition extends IGridFilterDefinitionBase<string[]> {
  dataType: "array";
  arrayType: "tag" | "assignee" | "team" | "role" | "goal" | "okr" | "user" | "okrStatus";
  placeholder?: string;
}

export type IArrayGridFilterDefinition =
  | IGeneralArrayGridFilterDefinition
  | IEntityArrayGridFilterDefinition
  | IStaticArrayGridFilterDefinition
  | ISessionArrayGridFilterDefinition;

export type ArrayFilterType = IArrayGridFilterDefinition["arrayType"];
export type ITextGridFilterView = IGridFilterViewBase<ITextGridFilter, ITextGridFilterDefinition, null>;

export interface ITextGridFilter extends IGridFilterBase<string> {
  dataType: "text";
}

export interface ITextGridFilterDefinition extends IGridFilterDefinitionBase<string> {
  dataType: "text";
  placeholder?: string;
}

export interface IBooleanGridFilter extends IGridFilterBase<boolean> {
  dataType: "boolean";
}

export interface IBooleanGridFilterDefinition extends IGridFilterDefinitionBase<boolean> {
  dataType: "boolean";
}

export type INumericGridFilterView = IGridFilterViewBase<INumericGridFilter, INumericGridFilterDefinition, number | null>;

export interface INumericGridFilter extends IGridFilterBase<number> {
  dataType: "numeric";
}

export interface INumericGridFilterDefinition extends IGridFilterDefinitionBase<number> {
  dataType: "numeric";
  percentage?: boolean;
}

export interface IDateRangeViewValue {
  isSelectingCustomDateRange: boolean;
  dateRange: IDateRange | string;
}

export type IDateGridFilterView = IGridFilterViewBase<IDateGridFilter, IDateGridFilterDefinition, string | IDateRangeViewValue>;

export type GridFilterPredefinedDateRange = { displayName: string; value: string };

export interface IDateGridFilter extends IGridFilterBase<string | IDateRange> {
  dataType: "date";
}

export interface IDateGridFilterDefinition extends IGridFilterDefinitionBase<string | IDateRange> {
  dataType: "date";
  predefinedDateRanges?: GridFilterPredefinedDateRange[];
  local?: boolean;
}

export type GridFilterEntry = string | { displayName: string; avatar?: TagAvatar; value: string | number | boolean };

export const entryValue = (entry: GridFilterEntry): string | number | boolean => (typeof entry === "string" ? entry : entry.value);

export const entryDisplayName = (entry: GridFilterEntry): string => (typeof entry === "string" ? entry : entry.displayName);

export type IEnumGridFilterView = IGridFilterViewBase<IEnumGridFilter, IEnumGridFilterDefinition, GridFilterEntry>;

export interface IEnumGridFilter extends IGridFilterBase<string | string[]> {
  dataType: "enum";
}

export interface IEnumGridFilterDefinition extends IGridFilterDefinitionBase<string> {
  dataType: "enum";
  entries: GridFilterEntry[];
}

export type IGridFilter = IArrayGridFilter | ITextGridFilter | IBooleanGridFilter | INumericGridFilter | IEnumGridFilter | IDateGridFilter;

export type GridFilterValue = IGridFilter["value"];

export type IGridFilterView = IGridFilterViewBase<IGridFilter, IGridFilterDefinition, unknown>;

export type IGridFilterDefinition =
  | IArrayGridFilterDefinition
  | ITextGridFilterDefinition
  | IBooleanGridFilterDefinition
  | INumericGridFilterDefinition
  | IEnumGridFilterDefinition
  | IDateGridFilterDefinition;

export type GridFilterDefinitions = Record<string, IGridFilterDefinition>;

export type GridFilterDataType = IGridFilter["dataType"];

export interface IDateRange {
  startDate: string;
  endDate: string;
}

export type GridFilterOperator =
  | "equals"
  | "notEqual"
  | "isOneOf"
  | "isNotOneOf"
  | "contains"
  | "doesNotContain"
  | "isBetween"
  | "isNotBetween"
  | "greater"
  | "less"
  | "isBefore"
  | "isAfter";

export const isPositiveOperator = (operator: GridFilterOperator): boolean =>
  (<GridFilterOperator[]>["notEqual", "isNotOneOf", "doesNotContain", "isNotBetween"]).every((negativeOperator) => negativeOperator !== operator);

export const isDateRangeOperator = (operator: GridFilterOperator): boolean => operator === "isBetween" || operator === "isNotBetween";

export const expandFilterValue = (filter: IArrayGridFilter): string[] => {
  const evaluatedDynamicValues = (filter.dynamicValues || []).reduce<string[]>((values, dynamicValue) => [...values, ...dynamicValue.evaluated], []);
  return unique([...filter.value, ...evaluatedDynamicValues]);
};

export type ItemType = "Objective" | "Key Result" | "Task" | "Employee" | "Team";

export type TagAvatar = AssigneeAvatar | { color: string } | { emoji: string } | { gtmhub: boolean };

export interface IGridFilterTag {
  id: string;
  name: string;
  avatar?: TagAvatar;
  dynamicValue?: IDynamicValue;
  isActive?: boolean;
}

export type QueryFn = (input: string, definition?: IArrayGridFilterDefinition, filter?: IArrayGridFilter) => IGridFilterTag[] | IPromise<IGridFilterTag[]>;

export interface IArrayGridFilterDataLoader {
  placeholder: string;
  query: QueryFn;
}

export const hasFilterValue = (filter: IGridFilter): boolean => {
  const hasDynamicValues = filter.dataType === "array" && (filter.dynamicValues || []).length > 0;
  if (hasDynamicValues) {
    return true;
  }

  const value = filter.value;
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (filter.dataType === "date") {
    return value && (typeof value !== "object" || !!value.startDate);
  }

  return value === 0 || value === false || !!value;
};

export const createFilterDefaultValue = (definition: IGridFilterDefinition, operator?: GridFilterOperator): GridFilterValue => {
  if (!operator) {
    operator = definition.defaultOperator;
  }

  if (typeof definition.defaultValue !== "undefined") {
    return copy(definition.defaultValue);
  }

  if (definition.dataType === "array") {
    return [];
  }

  if (definition.dataType === "date" && isDateRangeOperator(operator)) {
    return { startDate: undefined, endDate: undefined };
  }

  return undefined;
};

export const createFilter = (definition: IGridFilterDefinition): IGridFilter => ({
  dataType: definition.dataType,
  fieldName: definition.fieldName,
  operator: definition.defaultOperator,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: createFilterDefaultValue(definition) as any,
});

export interface IGridFiltersChange {
  initial: boolean;
}

export const okrStatusTagMap: Record<string, IGridFilterTag> = {
  // backend treats "" as "active" status
  "": {
    id: "",
    name: "Active",
  },
  achieved: {
    id: "achieved",
    name: "Achieved",
  },
  abandoned: {
    id: "abandoned",
    name: "Abandoned",
  },
};
